import { differenceInSeconds, formatDistance, parse } from 'date-fns';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {
  APP_DATE_FORMAT_HOUR_WITH_SEC,
  APP_DATE_FORMAT_VALIDATED,
  APP_DATE_FORMAT_WITHOUT_HOUR,
  APP_TIMESTAMP_FORMAT_YEAR_WITHOUT_SEC,
  APP_YEAR_FORMAT,
} from './../../config/constants';

import { APP_DATE_FORMAT, APP_DATE_FORMAT_HOUR, APP_LOCAL_DATETIME_FORMAT, APP_TIMESTAMP_FORMAT, APP_TIMESTAMP_FORMAT_YEAR } from 'app/config/constants';
import { pt } from 'date-fns/locale';

dayjs.extend(utc);
dayjs.extend(timezone);

const brtTimezone = 'America/Sao_Paulo';

export const convertDateTimeFromServer = date => (date ? dayjs(date).format(APP_LOCAL_DATETIME_FORMAT) : null);

export const convertDateTimeToServer = date => (date ? dayjs(date).toDate() : null);

export const displayDefaultDateTime = () => dayjs().startOf('day').format(APP_LOCAL_DATETIME_FORMAT);

export const displayDefaultTimeHourMin = () => dayjs().format(APP_DATE_FORMAT_HOUR);

export const convertDateTimeToPtBR = date => (date ? dayjs(date).format(APP_DATE_FORMAT) : null);

export const convertDateTimeToPtBRWithoutHour = date => (date ? dayjs(date).format(APP_DATE_FORMAT_WITHOUT_HOUR) : null);

export const convertDateTimeToHour = date => (date ? dayjs(date).format(APP_DATE_FORMAT_HOUR) : null);

export const convertDateTimeToPtBRWithYear = date => (date ? dayjs(date).format(APP_TIMESTAMP_FORMAT_YEAR) : null);

export const convertDateTimeToPtBRWithYearWithoutSeconds = date => (date ? dayjs(date).format(APP_TIMESTAMP_FORMAT_YEAR_WITHOUT_SEC) : null);

export const convertDateToTimestamp = date => date && new Date(date).getTime();

export const convertTimestampToDate = date => date && new Date(date).toISOString().replace(/\.\d{3}/, '');

export const convertYearFromServer = date => (date ? dayjs(date).format(APP_YEAR_FORMAT) : null);

export const convertDateFromServer = date => (date ? dayjs(date).format(APP_DATE_FORMAT_VALIDATED) : null);

export const convertDateTimeToPtBRWithSeconds = date => (date ? dayjs(date).format(APP_TIMESTAMP_FORMAT) : null);

export const convertDateTimeToHourWithMinAndSec = date => (date ? dayjs(date).format(APP_DATE_FORMAT_HOUR_WITH_SEC) : null);

export const minDateValue = parse('01-01-2020', 'dd-MM-yyyy', new Date()).toString();

export const convertDateTimeFromDate = date => (date ? dayjs(date).format(APP_DATE_FORMAT_VALIDATED) : null);

export const getYears = (): Array<number> => {
  const years: Array<number> = [];
  for (let year = new Date().getFullYear() + 2; year >= 1950; year--) {
    years.push(year);
  }
  years.unshift(null);
  return years;
};

export const convertTimeToHourMin = time => (time ? dayjs(`2000-01-01T${time}`).format(APP_DATE_FORMAT_HOUR) : null);

export const convertTimeToHourMinSec = time => (time ? dayjs(`2000-01-01T${time}`).format(APP_DATE_FORMAT_HOUR_WITH_SEC) : null);

export const convertTimeToHourMinBRT = time => (time ? dayjs.utc(time, APP_DATE_FORMAT_HOUR_WITH_SEC).tz(brtTimezone).format(APP_DATE_FORMAT_HOUR) : null);

export const convertTimeToHourMinSecUTC = time => (time ? dayjs.tz(time, APP_DATE_FORMAT_HOUR, brtTimezone).utc().format(APP_DATE_FORMAT_HOUR_WITH_SEC) : null);

export const differenceBetweenTwoDates = (startDate, finalDate, onReplace) => {
  if (!startDate || !finalDate) return '';

  const diffInSeconds = differenceInSeconds(new Date(startDate), new Date(finalDate));

  if (diffInSeconds < 60) {
    return `${diffInSeconds} seg`;
  }

  const diff = formatDistance(new Date(startDate), new Date(finalDate), { addSuffix: false, locale: pt, includeSeconds: true });

  if (onReplace) {
    return diff.replace('aproximadamente', ' ').replace('minutos', 'min');
  }

  return diff;
};

export const formatIdleTime = totalIdleTimeInSeconds => {
  const hours = Math.floor(totalIdleTimeInSeconds / 3600);
  const minutes = Math.floor((totalIdleTimeInSeconds % 3600) / 60);
  const seconds = totalIdleTimeInSeconds % 60;

  const parts = [];

  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? 'hora' : 'horas'}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`);
  }
  if (seconds > 0) {
    parts.push(`${seconds} ${seconds === 1 ? 'segundo' : 'segundos'}`);
  }

  if (parts.length > 1) {
    return [parts.slice(0, -1).join(' '), parts.slice(-1)].join(' e ');
  }

  return parts[0];
};

export function toRFC3339UTC(input: string): string {
  // If the string has no timezone, assume UTC by appending "Z"
  let normalized = input;
  if (!/[zZ]|[+-]\d{2}:\d{2}$/.test(input)) {
    normalized += 'Z';
  }

  const date = new Date(normalized);

  // Keep milliseconds — toISOString() already gives RFC 3339 with ms
  return date.toISOString();
}

export function toLocalISOString(date: Date | string) {
  const d = typeof date === 'string' ? new Date(date) : date;

  const pad = (n: number) => String(n).padStart(2, '0');

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` + `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function convertBRTToUTC(dateString: string): string {
  // Convert a date string from Brasilia timezone to UTC
  // The input should be in the format used by the frontend (YYYY-MM-DDTHH:mm)
  if (!dateString) return dateString;

  // Parse the date as if it's in Brasilia timezone and convert to UTC
  const dateInBRT = dayjs.tz(dateString, APP_LOCAL_DATETIME_FORMAT, brtTimezone);
  const dateInUTC = dateInBRT.utc();

  // Return in RFC3339 format for the backend
  return dateInUTC.toISOString();
}

export function convertUTCToBRT(dateString: string): string {
  // Convert a date string from UTC to Brasilia timezone
  // The input should be in UTC format (ISO string)
  if (!dateString) return dateString;

  // Parse the date as UTC and convert to Brasilia timezone
  const dateInUTC = dayjs.utc(dateString);
  const dateInBRT = dateInUTC.tz(brtTimezone);

  // Return in the format used by the frontend (YYYY-MM-DDTHH:mm)
  return dateInBRT.format(APP_LOCAL_DATETIME_FORMAT);
}
